import axios from '../utils/axiosConfig'; // global axios interceptors (401/403)
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// ---- cookie helpers ----
const isHttps = window.location.protocol === 'https:';
const COOKIE_ATTRS = `Path=/; SameSite=Strict; ${isHttps ? 'Secure;' : ''}`;

const setCookie = (name, value, maxAgeMs) => {
  const maxAgeSec = Math.max(0, Math.floor(maxAgeMs / 1000));
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSec}; ${COOKIE_ATTRS}`;
};
const getCookie = (name) => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
};
const delCookie = (name) => setCookie(name, '', 0);

// ---- client throttle (UX layer, per-email) ----
const WINDOW_MS = 5 * 60 * 1000; // rolling window
const MAX_ATTEMPTS = 3;          // max tries per window

const slug = (email) => email.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
const attemptsKey = (email) => `rp_attempts_${slug(email)}`;       // JSON: { c, t }
const cooldownKey = (email) => `rp_cooldown_until_${slug(email)}`; // epoch ms

// simple email format check
const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const ResetPasswordPage = () => {
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  // If user types an email that has an active cooldown, resume it
  useEffect(() => {
    if (!identifier) return;
    const key = cooldownKey(identifier);
    const untilStr = getCookie(key);
    const until = untilStr ? parseInt(untilStr, 10) : 0;
    const now = Date.now();
    if (until > now) {
      startCountdown(until - now, identifier);
    } else if (untilStr) {
      delCookie(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier]);

  const startCountdown = (ms, emailForCookie) => {
    setCooldown(true);
    setRemainingTime(Math.ceil(ms / 1000));

    // persist per-email cooldown
    if (emailForCookie) {
      setCookie(cooldownKey(emailForCookie), String(Date.now() + ms), ms);
    }

    const id = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          setCooldown(false);
          if (emailForCookie) delCookie(cooldownKey(emailForCookie));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const currentAttempts = (email) => {
    const raw = getCookie(attemptsKey(email));
    if (!raw) return 0;
    try {
      const obj = JSON.parse(raw);
      const now = Date.now();
      if (!obj.t || now - obj.t >= WINDOW_MS) return 0;
      return obj.c || 0;
    } catch {
      return 0;
    }
  };

  const bumpAttempts = (email) => {
    const key = attemptsKey(email);
    const now = Date.now();
    let c = 1, t = now;

    const raw = getCookie(key);
    if (raw) {
      try {
        const obj = JSON.parse(raw);
        if (obj.t && now - obj.t < WINDOW_MS) {
          c = Math.min(MAX_ATTEMPTS, (obj.c || 0) + 1);
          t = obj.t;
        }
      } catch {
        // ignore, start fresh
      }
    }
    const ttl = Math.max(0, WINDOW_MS - (now - t));
    setCookie(key, JSON.stringify({ c, t }), ttl);
    return c;
  };

  const clearMessagesOnType = (val) => {
    setIdentifier(val);
    setError('');
    setSuccess('');
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    if (loading || cooldown) return;

    setError('');
    setSuccess('');

    const email = identifier.trim().toLowerCase();
    if (!email) {
      setError('Please enter your email.');
      return;
    }
    if (!emailPattern.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Client-side throttle (UX): block if too many in rolling window
    const attempts = currentAttempts(email);
    if (attempts >= MAX_ATTEMPTS) {
      const ms = 300 * 1000; // 5 minutes
      startCountdown(ms, email);
      setError('Too many reset attempts. Please try again after 5 minutes.');
      return;
    }

    setLoading(true);
    // Record the attempt immediately to prevent button spam
    bumpAttempts(email);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/generate-reset-token/`,
        { email, frontend_url: window.location.origin },
        { headers: { 'X-Skip-Auth-Redirect': '1' }, __skipAuthRedirect: true }
      );

      // Enumeration-safe generic message
      setSuccess("If an account with that email exists, a password reset link has been sent.");
      setError('');

    } catch (error) {
      const resp = error.response;

      if (resp?.status === 429) {
        const ra = parseInt(
          resp.headers?.['retry-after'] || resp.headers?.['Retry-After'] || '0',
          10
        );
        const ms = ra > 0 ? ra * 1000 : 5 * 60 * 1000;
        startCountdown(ms, email);
        setError('Too many reset attempts. Please try again later.');
        return;
      }

      // For all other cases, keep it enumeration-safe with the same success message
      setSuccess("If the email exists, we've sent the reset link.");
      setError('');

    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="reset-password-container">
      <div className="reset-password-box">
        <Link to="/login" className="back-link">← Back to Login</Link>
        <h2>Reset Your Password</h2>

        {error && <p className="error-message">{error}</p>}
        {success && <p className="success-message">{success}</p>}

        <form onSubmit={handleResetRequest} className="reset-password-form" noValidate>
          <label htmlFor="identifier-input">Enter your email address *</label>
          <input
            type="email"
            id="identifier-input"
            value={identifier}
            onChange={(e) => clearMessagesOnType(e.target.value)}
            required
            placeholder="Enter your email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
          />


          <button type="submit" className="reset-button" disabled={loading || cooldown}>
            {loading
              ? 'Sending...'
              : cooldown
              ? `Try again in ${formatTime(remainingTime)}`
              : 'Send Reset Link'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
